import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { StepButton, StepLabel, StepIcon } from '@mui/material';
import CustomStepIcon from '#component/shipment/form/custom-step-icon';
import { OPEN_BACKDROP, RESET_PROJECT_FORM_DATA } from '#action/types';
import { goToProjectStep } from '#action/project/form';
import { reduxFormResolver } from '#helper/rhf-resolver';
import { Cnt, StyledStepper, StyledStep } from './styles';
import ProjectForm from './project';
import AlertsForm from './alerts';
import AlertConfirmationsForm from './alert-confirmation';
import ProductForm from './products';
import PackageForm from './packageTypes';
import ProjectCustomFieldsForm from './custom-fields';
import { setDataPost, isStepValid, isStepDirty, STEP_FIELDS } from './helper';
import validate from './validate';
import { useInitialValuesSync, useParentProjectWatch } from './use-project-form';
import ModulesHintDialog from './project/project-modules/modules-hint-dialog';
import { ErrorsContext } from './error-context';

const Project = props => {
  const { handleFormSubmit, initialValues, onParentProjectChange } = props;
  const account = useSelector(s => s.account);
  const [errors, setErrors] = useState({ alerts: [] });
  const [forceError, setForceError] = useState(false);
  const { match: { params: { id } } } = props;
  const step = useSelector(s => s.projectForm.step);
  const [showChangesHint, setShowChangesHint] = useState(false);
  const [changedModules, setChangedModules] = useState(null);
  const projects = useSelector(s => s.account.directProjects);
  const isRootProject = Boolean(projects.find(p => p.level === 0 && p.id === Number(id)));
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: reduxFormResolver((values, context) => validate(values, { ...props, ...context })),
    defaultValues: initialValues || {},
    mode: 'all'
  });

  const {
    watch,
    handleSubmit,
    getValues,
    getFieldState,
    trigger,
    formState: { errors: formErrors, dirtyFields, isDirty, isSubmitting }
  } = methods;

  useInitialValuesSync(methods, initialValues, Boolean(id));

  const formModules = watch('modules') ?? [];
  const initialFormModules = useMemo(() => initialValues?.modules ?? [], [initialValues?.modules]);
  useParentProjectWatch(watch('parentId'), onParentProjectChange);

  const steps = useMemo(() => {
    const alertConfirmAccess = account?.userType < 10 && account?.allowManageAlertAcceptance === 1;

    return [
      { name: 'project', label: 'Project' },
      { name: 'alerts', label: 'Alerts' },
      ...(alertConfirmAccess ? [{ name: 'alertConfirmation', label: 'Alert confirmations' }] : []
      ),
      { name: 'products', label: 'Products' },
      { name: 'customFields', label: 'Custom fields' },
      { name: 'packageType', label: 'Package type' }
    ];
  }, [account]);

  useEffect(() => () => {
    dispatch({ type: RESET_PROJECT_FORM_DATA });
  }, [dispatch, id]);

  const currentStep = steps[step] ?? steps[0];

  useEffect(() => {
    if (isStepValid(currentStep.name, formErrors)) setForceError(false);
  }, [currentStep.name, formErrors]);

  const errorContextValue = useMemo(() => ({ errors, setErrors }), [errors, setErrors]);

  const markAlertErrors = useCallback(alertErrors => {
    setErrors(s => {
      if (!s.alerts) return s;
      const alertsErrors =
        alertErrors?.map?.(error => !!Object.values(error ?? {}).length) ?? [];

      return { ...s, alerts: alertsErrors };
    });
  }, []);

  // Validates on demand rather than reading formState, which is still empty
  // before the first edit and would let an untouched required field slip past.
  const goToStep = async index => {
    if (await trigger(STEP_FIELDS[currentStep.name])) {
      dispatch(goToProjectStep(index));
    } else {
      setForceError(true);
      if (currentStep.name === 'alerts') {
        markAlertErrors(getFieldState('alerts').error);
      }
    }
  };

  const nextStep = () => goToStep(step + 1);

  const previousStep = () => goToStep(step - 1);

  const handleStepClick = index => () => goToStep(index);

  const onSubmitFail = useCallback(submissionErrors => {
    const invalidStep = steps.findIndex(({ name }) => !isStepValid(name, submissionErrors));
    if (invalidStep !== -1 && invalidStep !== step) {
      dispatch(goToProjectStep(invalidStep));
    }
    setForceError(true);
    markAlertErrors(submissionErrors?.alerts);
  }, [steps, step, dispatch, markAlertErrors]);

  const getStepProps = name => {
    const stepProps = {};
    const completed = !isStepDirty(name, dirtyFields);
    if (id && completed) {
      stepProps.completed = completed;
    }

    return stepProps;
  };

  const getChangedModules = (initModules, currentModules) => {
    const changed = [];
    currentModules.forEach(currModule => {
      const initModule = initModules.find(iM => iM.id === currModule.id);
      if (!currModule.checked && currModule.checked !== initModule?.checked) {
        changed.push(currModule);
      }
    });

    return changed;
  };

  const handleOpenHintModal = popupState => {
    setShowChangesHint(popupState);
  };

  const formSubmit = () => {
    dispatch({ type: OPEN_BACKDROP });
    handleFormSubmit(setDataPost(getValues(), isRootProject));
  };

  const onProjectSubmit = () => {
    const changed = getChangedModules(initialFormModules, formModules);
    if (changed?.length && id) {
      handleOpenHintModal(true);
      setChangedModules(changed);
    } else {
      formSubmit();
    }
  };

  const submitForm = handleSubmit(onProjectSubmit, onSubmitFail);

  const getFormStepComponents = () => {
    const commonProps = {
      formModules,
      forceError,
      setForceError,
      submitForm,
      submitting: isSubmitting,
      saveDisabled: isSubmitting || !isDirty
    };

    const formStepMap = {
      project: <ProjectForm
        {...props}
        {...commonProps}
        nextStep={nextStep}
      />,
      alerts: <AlertsForm
        {...props}
        {...commonProps}
        markAlertErrors={markAlertErrors}
        previousStep={previousStep}
        nextStep={nextStep}
      />,
      alertConfirmation: <AlertConfirmationsForm
        {...props}
        {...commonProps}
        previousStep={previousStep}
        nextStep={nextStep}
      />,
      products: <ProductForm
        {...props}
        {...commonProps}
        previousStep={previousStep}
        nextStep={nextStep}
      />,
      customFields: <ProjectCustomFieldsForm
        {...props}
        {...commonProps}
        previousStep={previousStep}
        nextStep={nextStep}
        setErrors={setErrors}
      />,
      packageType: <PackageForm
        {...props}
        {...commonProps}
        previousStep={previousStep}
      />
    };

    return steps.reduce((acc, { name }, index) => {
      acc[index] = formStepMap[name];
      return acc;
    }, {});
  };

  const formSteps = getFormStepComponents();

  return (
    <Cnt>
      <StyledStepper
        alternativeLabel
        nonLinear={!!id}
        activeStep={step}
      >
        {steps.map(({ label, name }, index) => (
          <StyledStep key={label} {...getStepProps(name)}>
            <StepButton disabled={!id || index === step} onClick={handleStepClick(index)}>
              <StepLabel StepIconComponent={id ? CustomStepIcon : StepIcon}>
                <span style={{ fontWeight: index === step ? 500 : 400 }}>{label}</span>
              </StepLabel>
            </StepButton>
          </StyledStep>
        ))}
      </StyledStepper>
      <FormProvider {...methods}>
        <ErrorsContext.Provider value={errorContextValue}>
          {formSteps[step]}
        </ErrorsContext.Provider>
      </FormProvider>
      {showChangesHint &&
        <ModulesHintDialog
          isOpen={showChangesHint}
          onAccept={formSubmit}
          onCancel={handleOpenHintModal}
          changedModules={changedModules}
        />}
    </Cnt>
  );
};

export default Project;
